import React from "react";
import styled from "@emotion/styled";
import PreviewSection from "../components/PreviewSection";
import {
  shadow,
  spacing,
  fontSize,
  fontWeight,
  fontFamily,
  borderRadius,
  lineHeight,
  lineHeightValues,
  letterSpacing,
  letterSpacingValues,
  duration,
  durationValues,
  easing,
  opacity,
  opacityValues,
  zIndex,
  layout,
  layoutValues,
  breakpointValues,
  componentBreakpointValues,
  dataViz,
  border as borderToken,
  brand,
  surface,
  text,
  interactive,
  status,
  nav,
  footer,
  colorPrimitives,
  useTheme,
} from "@atlas-int/tokens";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const SwatchGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[4]};
`;

const SwatchItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[1]};
`;

const SwatchColor = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.base};
  background-color: ${({ $color }) => $color};
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const SwatchLabel = styled.span`
  font-size: ${fontSize.xs};
  font-family: ${fontFamily.base};
  color: ${text.secondary};
  text-align: center;
  max-width: 80px;
  word-break: break-all;
`;

const SwatchValue = styled.span`
  font-size: ${fontSize.xs};
  font-family: ${fontFamily.mono};
  color: ${text.muted};
  text-align: center;
`;

const SpacingRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[2]};
`;

const SpacingBar = styled.div<{ $width: string }>`
  height: 16px;
  width: ${({ $width }) => $width};
  background-color: ${brand.accent};
  border-radius: ${borderRadius.base};
  min-width: 2px;
`;

const SpacingLabel = styled.span`
  font-size: ${fontSize.sm};
  font-family: ${fontFamily.mono};
  color: ${text.secondary};
  min-width: 120px;
`;

const TypographyRow = styled.div`
  margin-bottom: ${spacing[4]};
`;

const TypographyLabel = styled.span`
  font-size: ${fontSize.xs};
  font-family: ${fontFamily.mono};
  color: ${text.muted};
  display: block;
  margin-bottom: ${spacing[1]};
`;

const ShadowGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[8]};
`;

const ShadowBox = styled.div<{ $shadow: string }>`
  width: 120px;
  height: 80px;
  background-color: ${surface.raised};
  border-radius: ${borderRadius.md};
  box-shadow: ${({ $shadow }) => $shadow};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RadiusGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[6]};
  align-items: end;
`;

const RadiusBox = styled.div<{ $radius: string }>`
  width: 60px;
  height: 60px;
  background-color: ${brand.light};
  border-radius: ${({ $radius }) => $radius};
`;

const RadiusLabel = styled.div`
  text-align: center;
  margin-top: ${spacing[2]};
`;

const SubLabel = styled.span`
  font-size: ${fontSize.xs};
  font-family: ${fontFamily.mono};
  color: ${text.muted};
  display: block;
`;

const SubHeading = styled.h3`
  font-family: ${fontFamily.base};
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: ${spacing[8]} 0 ${spacing[4]} 0;
`;

const TokenTable = styled.table`
  border-collapse: collapse;
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.sm};
  color: ${text.primary};

  th,
  td {
    text-align: left;
    padding: ${spacing[2]} ${spacing[6]};
    border-bottom: 1px solid ${borderToken.subtle};
  }

  th {
    font-weight: ${fontWeight.semibold};
    color: ${text.secondary};
  }
`;

const OpacitySwatch = styled.div<{ $opacity: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.base};
  background-color: ${brand.accent};
  opacity: ${({ $opacity }) => $opacity};
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const AnimBox = styled.div<{ $dur: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.base};
  background-color: ${brand.accent};
  transition: transform ${({ $dur }) => $dur} ${easing.default};

  &:hover {
    transform: scale(1.5);
  }
`;

// ─── Component ───────────────────────────────────────────────────────────────

const TokensSection: React.FC = () => {
  // useTheme() gives us the resolved hex values for display in labels.
  // The actual swatch rendering uses CSS var() refs from tokens.ts.
  const theme = useTheme();

  const paletteEntries = Object.entries(colorPrimitives);
  // Flatten the grouped semantic color tokens into a single list for display
  const semanticGroups = { text, surface, border: borderToken, interactive, status, brand, nav, footer };
  const colorEntries = Object.entries(semanticGroups).flatMap(([groupName, group]) =>
    Object.entries(group).map(([key, value]) => [`${groupName}.${key}`, value])
  );
  const spacingEntries = Object.entries(spacing);
  const fontSizeEntries = Object.entries(fontSize);
  const shadowEntries = Object.entries(shadow);
  const radiusEntries = Object.entries(borderRadius);

  return (
    <>
      {/* Colors: Palette */}
      <PreviewSection
        id="tokens-palette"
        title="Palette Colors"
        description={`Raw color values — ${theme.name} theme. These are the building blocks; prefer semantic tokens in components.`}
      >
        <SwatchGrid>
          {paletteEntries.map(([name, varRef]) => (
            <SwatchItem key={name}>
              <SwatchColor $color={varRef} />
              <SwatchLabel>{name}</SwatchLabel>
              <SwatchValue>
                {varRef}
              </SwatchValue>
            </SwatchItem>
          ))}
        </SwatchGrid>
      </PreviewSection>

      {/* Colors: Semantic */}
      <PreviewSection
        id="tokens-colors"
        title="Semantic Colors"
        description={`Semantic color tokens — ${theme.name} theme. These map design intent to palette values.`}
      >
        <SwatchGrid>
          {colorEntries.map(([name, varRef]) => (
            <SwatchItem key={name}>
              <SwatchColor $color={varRef} />
              <SwatchLabel>{name}</SwatchLabel>
              <SwatchValue>
                {(() => {
                  const [group, key] = name.split(".");
                  const g = (theme.colors as unknown as Record<string, Record<string, string>>)[group];
                  return g?.[key] ?? varRef;
                })()}
              </SwatchValue>
            </SwatchItem>
          ))}
        </SwatchGrid>
      </PreviewSection>

      {/* Spacing */}
      <PreviewSection
        id="tokens-spacing"
        title="Spacing Scale"
        description="Spacing values used for margin, padding, and gap."
      >
        {spacingEntries.map(([key, value]) => (
          <SpacingRow key={key}>
            <SpacingLabel>
              spacing[{key}] = {value}
            </SpacingLabel>
            <SpacingBar $width={value} />
          </SpacingRow>
        ))}
      </PreviewSection>

      {/* Typography */}
      <PreviewSection
        id="tokens-typography"
        title="Typography"
        description="Font size scale with sample text rendered at each size."
      >
        {fontSizeEntries.map(([key, value]) => (
          <TypographyRow key={key}>
            <TypographyLabel>
              fontSize.{key} = {value}
            </TypographyLabel>
            <span
              style={{
                fontSize: value,
                fontFamily: fontFamily.base,
                color: text.primary,
              }}
            >
              The quick brown fox jumps over the lazy dog
            </span>
          </TypographyRow>
        ))}

        <SubHeading>Font Weights</SubHeading>
        {Object.entries(fontWeight).map(([key, value]) => (
          <TypographyRow key={key}>
            <TypographyLabel>
              fontWeight.{key} = {value}
            </TypographyLabel>
            <span
              style={{
                fontSize: fontSize.lg,
                fontFamily: fontFamily.base,
                fontWeight: value,
                color: text.primary,
              }}
            >
              The quick brown fox jumps over the lazy dog
            </span>
          </TypographyRow>
        ))}

        <SubHeading>Font Families</SubHeading>
        {Object.entries(fontFamily).map(([key, value]) => (
          <TypographyRow key={key}>
            <TypographyLabel>
              fontFamily.{key} = {value}
            </TypographyLabel>
            <span
              style={{
                fontSize: fontSize.lg,
                fontFamily: value,
                color: text.primary,
              }}
            >
              The quick brown fox jumps over the lazy dog
            </span>
          </TypographyRow>
        ))}
      </PreviewSection>

      {/* Border Radius */}
      <PreviewSection
        id="tokens-border-radius"
        title="Border Radius"
        description="Border radius tokens for controlling element roundness."
      >
        <RadiusGrid>
          {radiusEntries.map(([key, value]) => (
            <div key={key}>
              <RadiusBox $radius={value} />
              <RadiusLabel>
                <SwatchLabel>{key}</SwatchLabel>
                <SubLabel>{value}</SubLabel>
              </RadiusLabel>
            </div>
          ))}
        </RadiusGrid>
      </PreviewSection>

      {/* Shadows */}
      <PreviewSection
        id="tokens-shadows"
        title="Shadows"
        description={`Box shadow tokens — ${theme.name} theme. Stronger in dark mode for visibility.`}
      >
        <ShadowGrid>
          {shadowEntries.map(([key, varRef]) => (
            <div key={key}>
              <ShadowBox $shadow={varRef}>
                <SwatchLabel>{key}</SwatchLabel>
              </ShadowBox>
              <SubLabel style={{ marginTop: spacing[2], display: "block" }}>
                {(theme.shadow as unknown as Record<string, string>)[key] ?? varRef}
              </SubLabel>
            </div>
          ))}
        </ShadowGrid>
      </PreviewSection>

      {/* Line Height */}
      <PreviewSection
        id="tokens-line-height"
        title="Line Height"
        description="Line height tokens controlling vertical rhythm."
      >
        {Object.entries(lineHeight)
          .filter(([key]) => key !== "body")
          .map(([key, value]) => (
            <TypographyRow key={key}>
              <TypographyLabel>
                lineHeight.{key} ={" "}
                {lineHeightValues[key as keyof typeof lineHeightValues]}
              </TypographyLabel>
              <span
                style={{
                  fontSize: fontSize.base,
                  fontFamily: fontFamily.base,
                  lineHeight: value,
                  color: text.primary,
                  display: "inline-block",
                  maxWidth: "500px",
                }}
              >
                The quick brown fox jumps over the lazy dog. This sample text
                wraps to show the effect of different line-height values on
                multi-line content.
              </span>
            </TypographyRow>
          ))}
      </PreviewSection>

      {/* Letter Spacing */}
      <PreviewSection
        id="tokens-letter-spacing"
        title="Letter Spacing"
        description="Letter spacing tokens for typographic control."
      >
        {Object.entries(letterSpacing).map(([key, value]) => (
          <TypographyRow key={key}>
            <TypographyLabel>
              letterSpacing.{key} ={" "}
              {letterSpacingValues[key as keyof typeof letterSpacingValues]}
            </TypographyLabel>
            <span
              style={{
                fontSize: fontSize.lg,
                fontFamily: fontFamily.base,
                letterSpacing: value,
                color: text.primary,
              }}
            >
              ATLAS INTERNATIONAL TRADE
            </span>
          </TypographyRow>
        ))}
      </PreviewSection>

      {/* Duration / Animation */}
      <PreviewSection
        id="tokens-animation"
        title="Duration &amp; Animation"
        description="Transition duration tokens and easing curves. Hover each box to see the transition speed."
      >
        <SwatchGrid>
          {Object.entries(duration).map(([key, value]) => (
            <SwatchItem key={key}>
              <AnimBox $dur={value} />
              <SwatchLabel>{key}</SwatchLabel>
              <SwatchValue>
                {durationValues[key as keyof typeof durationValues]}
              </SwatchValue>
            </SwatchItem>
          ))}
        </SwatchGrid>
        <SubHeading>Easing</SubHeading>
        <TokenTable>
          <thead>
            <tr>
              <th>Token</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(easing).map(([key, value]) => (
              <tr key={key}>
                <td>easing.{key}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </TokenTable>
      </PreviewSection>

      {/* Opacity */}
      <PreviewSection
        id="tokens-opacity"
        title="Opacity"
        description="Opacity tokens for controlling element transparency."
      >
        <SwatchGrid>
          {Object.entries(opacity).map(([key, value]) => (
            <SwatchItem key={key}>
              <OpacitySwatch $opacity={value} />
              <SwatchLabel>{key}</SwatchLabel>
              <SwatchValue>
                {opacityValues[key as keyof typeof opacityValues]}
              </SwatchValue>
            </SwatchItem>
          ))}
        </SwatchGrid>
      </PreviewSection>

      {/* Z-Index */}
      <PreviewSection
        id="tokens-z-index"
        title="Z-Index"
        description="Z-index scale for managing stacking contexts."
      >
        <TokenTable>
          <thead>
            <tr>
              <th>Token</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(zIndex).map(([key, value]) => (
              <tr key={key}>
                <td>zIndex.{key}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </TokenTable>
      </PreviewSection>

      {/* Layout */}
      <PreviewSection
        id="tokens-layout"
        title="Layout"
        description="Layout constants for structural dimensions."
      >
        <TokenTable>
          <thead>
            <tr>
              <th>Token</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(layout).map(([key, value]) => (
              <tr key={key}>
                <td>layout.{key}</td>
                <td>
                  {layoutValues[key as keyof typeof layoutValues]} ({value})
                </td>
              </tr>
            ))}
          </tbody>
        </TokenTable>
      </PreviewSection>

      {/* Breakpoints */}
      <PreviewSection
        id="tokens-breakpoints"
        title="Breakpoints"
        description="Responsive breakpoints for media queries."
      >
        <SubHeading>Global Scale</SubHeading>
        <TokenTable>
          <thead>
            <tr>
              <th>Token</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(breakpointValues).map(([key, value]) => (
              <tr key={key}>
                <td>breakpoints.{key}</td>
                <td>{value}px</td>
              </tr>
            ))}
          </tbody>
        </TokenTable>
        <SubHeading>Component-Specific</SubHeading>
        <TokenTable>
          <thead>
            <tr>
              <th>Token</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(componentBreakpointValues).map(([key, value]) => (
              <tr key={key}>
                <td>componentBreakpoints.{key}</td>
                <td>{value}px</td>
              </tr>
            ))}
          </tbody>
        </TokenTable>
      </PreviewSection>

      {/* Data Viz Colors */}
      <PreviewSection
        id="tokens-data-viz"
        title="Data Viz Colors"
        description="Semantic color tokens for visualization elements (not themed)."
      >
        <SwatchGrid>
          {Object.entries(dataViz).map(([name, value]) => (
            <SwatchItem key={name}>
              <SwatchColor $color={value} />
              <SwatchLabel>{name}</SwatchLabel>
              <SwatchValue>{value}</SwatchValue>
            </SwatchItem>
          ))}
        </SwatchGrid>
      </PreviewSection>
    </>
  );
};

export default TokensSection;
